import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  AlertTriangle,
  BarChart3,
  ChevronDown,
  Landmark,
  Building2,
  CalendarClock,
  Ellipsis,
  FileCheck2,
  Fuel,
  Keyboard,
  LayoutDashboard,
  LogOut,
  Map as MapIcon,
  Moon,
  Package,
  PlugZap,
  Route as RouteIcon,
  Search,
  Sun,
  Truck,
  User,
  UserCog,
  Wrench,
  Ticket,
} from 'lucide-react';
import type { UserRole } from '@kmdx/contracts';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';
import { useHotkeys, useHotkeyList, keyLabel } from '@/lib/hotkeys';
import { useUi } from '@/stores/ui';
import { useSession } from '@/api/session';
import { useLogout, useOrg } from '@/api/hooks/auth';
import { useAnomalies } from '@/api/hooks/anomalies';
import { Button, IconButton } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { GlobalSearch } from './GlobalSearch';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Package;
  chord: string;
  group: 'work' | 'money' | 'setup';
  /** Кратко име за долната лента на телефона, ако етикетът не се събира. */
  short?: string;
  /** Само за тези роли; без списък — за всички в офиса. */
  roles?: UserRole[];
}

/**
 * Менюто: отгоре само петте екрана, които се отварят всеки ден — Начало
 * (бусовете), Заявки, Документи, Справки, Аномалии. Решение на клиента:
 * „Началото да са всички бусове… доста от страниците може да се скрият, ако
 * информацията я има вече в бусa". Всичко останало е в сгънатото „Още" най-
 * долу, подредено в трите групи — не е изтрито: импортът от AS 24, ДДС и
 * потребителите нямат друг вход. Калкулаторът за оферта е бутон в „Заявки".
 */
const NAV: NavItem[] = [
  { to: '/', label: t.nav.board, icon: LayoutDashboard, chord: 'g b', group: 'work', roles: ['owner', 'dispatcher'] },
  { to: '/orders', label: t.nav.orders, icon: Package, chord: 'g o', group: 'work' },
  { to: '/trips', label: t.nav.trips, icon: RouteIcon, chord: 'g k', group: 'work' },
  { to: '/map', label: t.nav.map, icon: MapIcon, chord: 'g m', group: 'work', roles: ['owner', 'dispatcher'] },
  { to: '/service', label: 'Сервиз', icon: Wrench, chord: 'g w', group: 'work', roles: ['owner', 'dispatcher', 'accountant', 'mechanic'] },
  { to: '/anomalies', label: 'Аномалии', icon: AlertTriangle, chord: 'g a', group: 'work', roles: ['owner', 'dispatcher', 'accountant'] },
  { to: '/documents', label: t.nav.documents, short: 'Документи', icon: FileCheck2, chord: 'g d', group: 'money' },
  { to: '/dues', label: t.nav.dues, icon: CalendarClock, chord: 'g u', group: 'money', roles: ['owner', 'accountant'] },
  { to: '/vignettes', label: 'Винетки', icon: Ticket, chord: 'g n', group: 'money', roles: ['owner', 'dispatcher', 'accountant'] },
  { to: '/vat', label: 'ДДС от чужбина', icon: Landmark, chord: 'g t', group: 'money', roles: ['owner', 'accountant'] },
  { to: '/fuel', label: t.nav.fuel, icon: Fuel, chord: 'g f', group: 'money' },
  { to: '/reports', label: t.nav.reports, icon: BarChart3, chord: 'g r', group: 'money' },
  { to: '/vehicles', label: t.nav.vehicles, icon: Truck, chord: 'g v', group: 'setup' },
  { to: '/drivers', label: t.nav.drivers, icon: User, chord: 'g s', group: 'setup' },
  { to: '/partners', label: t.nav.partners, icon: Building2, chord: 'g p', group: 'setup' },
  { to: '/settings/users', label: 'Потребители', icon: UserCog, chord: 'g x', group: 'setup', roles: ['owner'] },
  { to: '/settings/integrations', label: t.nav.integrations, icon: PlugZap, chord: 'g i', group: 'setup', roles: ['owner'] },
];

const GROUPS: { key: NavItem['group']; label: string }[] = [
  { key: 'work', label: t.nav.groupWork },
  { key: 'money', label: t.nav.groupMoney },
  { key: 'setup', label: t.nav.groupSetup },
];

/**
 * Без списък — за офиса. Майсторът е изключение: той вижда САМО изрично
 * разрешеното (сервиза), не заявки, клиенти и пари.
 */
function allowed(item: NavItem, role: UserRole | undefined): boolean {
  if (role === 'mechanic') return item.roles?.includes('mechanic') ?? false;
  return !item.roles || (role !== undefined && item.roles.includes(role));
}

export const ROLE_TITLE: Partial<Record<UserRole, string>> = {
  owner: 'Управител',
  dispatcher: 'Диспечер',
  accountant: 'Счетоводител',
  mechanic: 'Майстор',
};

/**
 * ─────────────────────────────────────────────────────────────────────────
 *  Навигацията на тесен екран: ДОЛНА ЛЕНТА, не хамбургер.
 *
 *  Изборът не е вкусов. Диспечерът отваря борда с една ръка, вечер, докато
 *  държи телефона ниско — палецът стига долния край, но не и горния ляв ъгъл,
 *  където живее хамбургерът. По-важното: долната лента ПОКАЗВА къде се намира
 *  човекът и колко други места има, без да се отваря нищо. Хамбургерът скрива
 *  и състоянието, и списъка зад едно допълнително докосване, а „кой е в Борда
 *  и кой в Заявките" е въпрос, който се задава по десет пъти на смяна.
 *
 *  Четирите най-често отваряни екрана стоят в лентата (Начало, Заявки,
 *  Документи, Аномалии); останалите са в „Още" — лист, който излиза отдолу. Търсенето
 *  НЕ е в „Още": спедиторът казва номер по телефона и полето трябва да е на
 *  екрана още в същата секунда, затова стои в горната лента винаги.
 *
 *  На широк екран нищо не се променя — страничната лента остава каквато беше.
 * ─────────────────────────────────────────────────────────────────────────
 */
/**
 * Основните екрани по роля — винаги видими. Счетоводителят не разпределя
 * бусове: неговото „Начало" е „Падежи". Майсторът вижда само сервиза.
 */
function mainPathsFor(role: UserRole | undefined): string[] {
  if (role === 'mechanic') return ['/service'];
  if (role === 'accountant') return ['/dues', '/orders', '/documents', '/reports', '/anomalies'];
  return ['/', '/orders', '/documents', '/reports', '/anomalies'];
}

function navFor(paths: string[], role: UserRole | undefined): NavItem[] {
  return paths
    .map((path) => NAV.find((item) => item.to === path))
    .filter((item): item is NavItem => item !== undefined && allowed(item, role));
}

function mainNavFor(role: UserRole | undefined): NavItem[] {
  return navFor(mainPathsFor(role), role);
}

/** Сгънатото „Още": всичко разрешено, което не е между основните. */
function moreNavFor(role: UserRole | undefined): NavItem[] {
  const main = mainPathsFor(role);
  return NAV.filter((item) => !main.includes(item.to) && allowed(item, role));
}

/**
 * Долната лента на телефона има четири места и „Още": петият основен екран
 * (за офиса — „Справки", работа на бюро) отива в листа „Още" най-отгоре.
 */
function primaryPathsFor(role: UserRole | undefined): string[] {
  if (role === 'mechanic') return ['/service'];
  return role === 'accountant' ? ['/dues', '/orders', '/documents', '/reports'] : ['/', '/orders', '/documents', '/anomalies'];
}

function primaryNavFor(role: UserRole | undefined): NavItem[] {
  return navFor(primaryPathsFor(role), role);
}

/** Листът „Още" на телефона: първо останалите основни, после сгънатите. */
function secondaryNavFor(role: UserRole | undefined): NavItem[] {
  const primary = primaryPathsFor(role);
  return [...mainNavFor(role), ...moreNavFor(role)].filter((item) => !primary.includes(item.to));
}

const MORE_OPEN_KEY = 'kmdx.nav.moreOpen';

function readMoreOpen(): boolean {
  try {
    return window.localStorage.getItem(MORE_OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMoreOpen(open: boolean): void {
  try {
    window.localStorage.setItem(MORE_OPEN_KEY, open ? '1' : '0');
  } catch {
    /* частен прозорец или забранено хранилище — менюто просто се отваря сгънато */
  }
}

/** Началният екран на ролята: за счетоводителя — „Падежи". */
export function homePathFor(role: UserRole | undefined): string {
  if (role === 'mechanic') return '/service';
  return role === 'accountant' ? '/dues' : '/';
}

function isActive(pathname: string, to: string): boolean {
  /** Страницата на буса е продължение на „Начало" — оттам се стига до нея, не от списъка „Бусове". */
  if (to === '/') return pathname === '/' || /^\/vehicles\/[^/]+/.test(pathname);
  if (to === '/vehicles') return pathname === '/vehicles';
  return pathname.startsWith(to);
}

/** Един ред от страничната лента — с ивица отляво, когато е отворен, и значка с брой. */
function SidebarLink({ item, active, badge }: { item: NavItem; active: boolean; badge?: number }) {
  const Icon = item.icon;
  return (
    <li>
      <Link
        to={item.to}
        className={cn(
          'group relative flex items-center gap-3 rounded-lg px-2.5 py-2 text-[13.5px] transition-colors',
          active ? 'bg-accent-soft font-medium text-accent' : 'text-ink-dim hover:bg-raised hover:text-ink',
        )}
      >
        {active ? <span className="absolute inset-y-1.5 left-0 w-[3px] rounded-full bg-accent" aria-hidden="true" /> : null}
        <Icon className="size-[18px] shrink-0" />
        <span className="flex-1 truncate">{item.label}</span>
        {badge ? (
          <span className="rounded-full bg-danger px-1.5 text-[10px] font-bold leading-4 text-white">{badge > 99 ? '99+' : badge}</span>
        ) : null}
      </Link>
    </li>
  );
}

export function AppShell() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const theme = useUi((s) => s.theme);
  const toggleTheme = useUi((s) => s.toggleTheme);
  const searchOpen = useUi((s) => s.searchOpen);
  const setSearchOpen = useUi((s) => s.setSearchOpen);
  const shortcutsOpen = useUi((s) => s.shortcutsOpen);
  const setShortcutsOpen = useUi((s) => s.setShortcutsOpen);
  const moreOpen = useUi((s) => s.moreOpen);
  const setMoreOpen = useUi((s) => s.setMoreOpen);
  const user = useSession((s) => s.user);
  const logout = useLogout();
  const org = useOrg();

  // Смяна на екрана затваря листа „Още" — иначе той остава върху новия екран.
  useEffect(() => {
    setMoreOpen(false);
  }, [pathname, setMoreOpen]);

  /** Майсторът няма работа извън сервиза — всеки друг адрес го връща там. */
  useEffect(() => {
    if (user?.role === 'mechanic' && !pathname.startsWith('/service')) {
      void navigate({ to: '/service', replace: true });
    }
  }, [user?.role, pathname, navigate]);

  /** Търсенето обхожда заявки, клиенти и курсове — майсторът няма достъп до тях. */
  const canSearch = user?.role !== 'mechanic';

  useHotkeys([
    ...(canSearch
      ? [
          {
            keys: 'mod+k',
            description: t.common.search,
            group: 'Общи',
            allowInInput: true,
            run: () => setSearchOpen(true),
          },
          { keys: '/', description: t.common.search, group: 'Общи', run: () => setSearchOpen(true) },
          {
            keys: 'n',
            description: t.orders.newOrder,
            group: 'Общи',
            run: () => void navigate({ to: '/orders/new' }),
          },
        ]
      : []),
    {
      keys: '?',
      description: t.common.shortcuts,
      group: 'Общи',
      run: () => setShortcutsOpen(true),
    },
    ...NAV.filter((item) => allowed(item, user?.role)).map((item) => ({
      keys: item.chord,
      description: item.label,
      group: 'Навигация',
      run: () => void navigate({ to: item.to }),
    })),
  ]);

  const secondaryNav = secondaryNavFor(user?.role);
  /** Долната лента: „Още" свети, когато екранът е в листа. */
  const sheetActive = secondaryNav.some((item) => isActive(pathname, item.to));
  /** Страничната лента: сгънатото „Още" и дали отвореният екран е в него. */
  const moreNav = moreNavFor(user?.role);
  const moreActive = moreNav.some((item) => isActive(pathname, item.to));
  const [moreStored, setMoreStored] = useState(readMoreOpen);
  /** Отвореният екран е в „Още" — списъкът стои разгънат, за да се вижда къде си. */
  const sidebarMoreOpen = moreStored || moreActive;
  const toggleSidebarMore = () => {
    const next = !sidebarMoreOpen;
    setMoreStored(next);
    writeMoreOpen(next);
  };
  /** Значката до „Аномалии": колко важни чакат преглед. Майсторът няма достъп до тях. */
  const anomalies = useAnomalies(false, user?.role !== undefined && user.role !== 'mechanic');
  const badges: Record<string, number> = { '/anomalies': anomalies.data?.counts.high ?? 0 };

  return (
    <div className="flex h-full min-h-0 bg-ground">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-line bg-surface lg:flex">
        <div className="flex h-[4.5rem] items-center border-b border-line px-4">
          <Link to={homePathFor(user?.role)} aria-label={t.nav.board}>
            <BrandLogo size="md" />
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          <ul className="space-y-0.5">
            {mainNavFor(user?.role).map((item) => (
              <SidebarLink key={item.to} item={item} active={isActive(pathname, item.to)} badge={badges[item.to]} />
            ))}
          </ul>

          {moreNav.length > 0 ? (
            <div className="mt-4 border-t border-line pt-2">
              <button
                type="button"
                onClick={toggleSidebarMore}
                aria-expanded={sidebarMoreOpen}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-[13.5px] transition-colors hover:bg-raised hover:text-ink',
                  moreActive ? 'font-medium text-accent' : 'text-ink-dim',
                )}
              >
                <Ellipsis className="size-[18px] shrink-0" />
                <span className="flex-1 text-left">Още</span>
                <span className="rounded-full bg-raised px-1.5 text-[10px] text-ink-faint">{moreNav.length}</span>
                <ChevronDown className={cn('size-4 shrink-0 transition-transform', sidebarMoreOpen && 'rotate-180')} />
              </button>
              {sidebarMoreOpen ? (
                <div className="mt-1">
                  {GROUPS.map((group) => {
                    const items = moreNav.filter((item) => item.group === group.key);
                    if (items.length === 0) return null;
                    return (
                      <div key={group.key} className="mb-2">
                        <div className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-faint">
                          {group.label}
                        </div>
                        <ul className="space-y-0.5">
                          {items.map((item) => (
                            <SidebarLink key={item.to} item={item} active={isActive(pathname, item.to)} badge={badges[item.to]} />
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
        </nav>

        <div className="border-t border-line p-2 text-[11px] text-ink-faint">
          <div className="truncate px-1.5">{org.data?.legalName ?? t.app.company}</div>
          {org.data?.communityLicenceNo ? (
            <div className="truncate px-1.5">
              Лиценз {org.data.communityLicenceNo}
              {org.data.communityLicenceCopies ? ` · ${org.data.communityLicenceCopies} копия` : ''}
            </div>
          ) : null}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line bg-surface px-3 sm:gap-3 sm:px-4">
          <Link to={homePathFor(user?.role)} className="shrink-0 lg:hidden" aria-label={t.nav.board}>
            <BrandLogo size="sm" />
          </Link>
          {canSearch ? (
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            /*
              `min-w-0` не е разкрасяване. Плейсхолдърът вътре е `truncate`, а
              `white-space: nowrap` прави мин-съдържанието на бутона по-широко
              от реда. Без `min-w-0` гъвкавият елемент отказва да се свие и на
              390 px хедърът стърчи с 6 px. Толкова стигат: Chrome разширява
              подредбата до 396 px, страницата се смалява 1,5 % и долната лента
              слиза на 857 px при 844 px екран — надписите „Борд“ и „Заявки“
              се режат по средата. Един клас държи цялата подредба на място.
            */
            className="flex h-10 w-full min-w-0 max-w-md items-center gap-2 rounded-md border border-line-strong bg-ground px-2.5 text-left text-sm text-ink-faint hover:border-accent/50 lg:h-8"
          >
            <Search className="size-4 shrink-0" />
            <span className="flex-1 truncate">{t.common.searchPlaceholder}</span>
            <kbd className="hidden rounded border border-line px-1 font-mono text-[10px] lg:inline">
              Ctrl K
            </kbd>
          </button>
          ) : (
            <span className="flex-1" />
          )}

          <div className="ml-auto hidden items-center gap-1 lg:flex">
            <IconButton label={t.common.shortcuts} onClick={() => setShortcutsOpen(true)}>
              <Keyboard className="size-4" />
            </IconButton>
            <IconButton label={t.common.theme} onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </IconButton>
            <div className="mx-1 text-right">
              <div className="text-xs font-medium leading-tight">{user?.fullName ?? '—'}</div>
              <div className="text-[10px] uppercase tracking-wide text-ink-faint">
                {(user?.role && ROLE_TITLE[user.role]) ?? 'Диспечер'}
              </div>
            </div>
            <IconButton label={t.common.logout} onClick={() => logout.mutate()}>
              <LogOut className="size-4" />
            </IconButton>
          </div>
        </header>

        {/*
          Отдолу се пази точно толкова място, колкото заема лентата плюс зоната
          на жеста „начало" — иначе последният ред стои под нея и не се докосва.
        */}
        <main className="min-h-0 flex-1 overflow-y-auto pb-[calc(3.5rem+env(safe-area-inset-bottom))] lg:pb-0">
          <Outlet />
        </main>
      </div>

      <nav
        aria-label={t.app.subtitle}
        className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] lg:hidden"
      >
        <ul className="flex">
          {primaryNavFor(user?.role).map((item) => {
            const active = isActive(pathname, item.to);
            const Icon = item.icon;
            return (
              <li key={item.to} className="flex-1">
                <Link
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex h-14 flex-col items-center justify-center gap-0.5 border-t-2 text-[11px] transition-colors',
                    active
                      ? 'border-accent bg-accent-soft text-accent'
                      : 'border-transparent text-ink-dim',
                  )}
                >
                  <Icon className="size-5" />
                  <span className="leading-none">{item.short ?? item.label}</span>
                </Link>
              </li>
            );
          })}
          <li className="flex-1">
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-expanded={moreOpen}
              className={cn(
                'flex h-14 w-full flex-col items-center justify-center gap-0.5 border-t-2 text-[11px] transition-colors',
                sheetActive
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-transparent text-ink-dim',
              )}
            >
              <Ellipsis className="size-5" />
              <span className="leading-none">Още</span>
            </button>
          </li>
        </ul>
      </nav>

      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} pathname={pathname} />
      {canSearch ? <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} /> : null}
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}

/** Останалите екрани плюс настройките на сесията — само за тесен екран. */
function MoreSheet({
  open,
  onClose,
  pathname,
}: {
  open: boolean;
  onClose: () => void;
  pathname: string;
}) {
  const theme = useUi((s) => s.theme);
  const toggleTheme = useUi((s) => s.toggleTheme);
  const user = useSession((s) => s.user);
  const logout = useLogout();
  const org = useOrg();

  return (
    <Dialog open={open} onClose={onClose} title="Още" width="lg">
      <ul className="grid grid-cols-2 gap-2">
        {secondaryNavFor(user?.role).map((item) => {
          const active = isActive(pathname, item.to);
          const Icon = item.icon;
          return (
            <li key={item.to}>
              <Link
                to={item.to}
                onClick={onClose}
                data-tap
                className={cn(
                  'flex min-h-[3.25rem] items-center gap-2.5 rounded-lg border px-3 py-2 text-sm',
                  active
                    ? 'border-accent/50 bg-accent-soft text-accent'
                    : 'border-line text-ink-dim',
                )}
              >
                <Icon className="size-5 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 border-t border-line pt-3">
        <div className="mb-3 text-sm">
          <div className="font-medium text-ink">{user?.fullName ?? '—'}</div>
          <div className="text-[11px] uppercase tracking-wide text-ink-faint">
            {(user?.role && ROLE_TITLE[user.role]) ?? 'Диспечер'}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            icon={theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
            onClick={toggleTheme}
          >
            {t.common.theme}
          </Button>
          <Button
            variant="secondary"
            icon={<LogOut className="size-4" />}
            onClick={() => logout.mutate()}
          >
            {t.common.logout}
          </Button>
        </div>

        <div className="mt-3 text-[11px] text-ink-faint">
          <div className="truncate">{org.data?.legalName ?? t.app.company}</div>
          {org.data?.communityLicenceNo ? (
            <div className="truncate">
              Лиценз {org.data.communityLicenceNo}
              {org.data.communityLicenceCopies ? ` · ${org.data.communityLicenceCopies} копия` : ''}
            </div>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}

function ShortcutsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const hotkeys = useHotkeyList();
  const groups = useMemo(() => {
    const map = new Map<string, { keys: string; description: string }[]>();
    for (const hotkey of hotkeys) {
      const group = hotkey.group ?? 'Екран';
      const list = map.get(group) ?? [];
      if (!list.some((h) => h.keys === hotkey.keys)) {
        list.push({ keys: hotkey.keys, description: hotkey.description });
      }
      map.set(group, list);
    }
    return [...map.entries()];
  }, [hotkeys]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.common.shortcuts}
      description="Комбинациите за екрана, който е отворен в момента, са най-отдолу."
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t.common.close}
        </Button>
      }
    >
      <div className="space-y-4">
        {groups.map(([group, items]) => (
          <div key={group}>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-ink-faint">{group}</div>
            <ul className="divide-y divide-line">
              {items.map((item) => (
                <li key={item.keys} className="flex items-center justify-between gap-4 py-1.5">
                  <span className="text-sm text-ink-dim">{item.description}</span>
                  <kbd className="shrink-0 rounded border border-line-strong bg-raised px-1.5 py-0.5 font-mono text-[11px]">
                    {keyLabel(item.keys)}
                  </kbd>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Dialog>
  );
}
